import * as Tooltip from '@radix-ui/react-tooltip'
import { GitMerge } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { useLocation } from '#router'
import { useProblemListsOptional } from '@/components/problem-lists/problem-list-context'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { trpc } from '@/lib/trpc'

interface MergeButtonProps {
  sourceType: 'list' | 'topic' | 'subtopic' | 'chapter'
  sourceId: string
  className?: string
}

export function MergeButton({
  sourceType,
  sourceId,
  className,
}: MergeButtonProps) {
  const ctx = useProblemListsOptional()
  const utils = trpc.useUtils()
  const { navigate } = useLocation()
  const [open, setOpen] = useState(false)

  const mergeMutation = trpc.lists.merge.useMutation({
    onSuccess: (data) => {
      const msg = `Merged ${data.merged} problem${data.merged !== 1 ? 's' : ''}${data.skipped > 0 ? ` (${data.skipped} already present)` : ''}`
      toast.success(msg)
      utils.lists.getUserListData.invalidate()
      utils.lists.getListDetail.invalidate()
      setOpen(false)

      // Navigate to the target list if the source was deleted
      if (data.sourceDeleted) {
        navigate(`/problems/lists/${data.targetListId}`)
      }
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to merge')
    },
  })

  // Don't render if user is not authenticated (no provider)
  if (!ctx) return null

  // Filter out the source list from targets (only applies when source is a list)
  const targets = ctx.lists.filter(
    (list) => sourceType !== 'list' || list.id !== sourceId,
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip.Provider delayDuration={150}>
        <Tooltip.Root>
          {/* Both asChild props required: merges refs/handlers onto Button without extra DOM elements */}
          <Tooltip.Trigger asChild>
            <PopoverTrigger asChild>
              <Button
                size="icon-sm"
                variant="outline"
                className={className}
                disabled={mergeMutation.isPending}
                aria-label="Merge"
              >
                <GitMerge className="h-4 w-4 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              side="top"
              align="center"
              className="border-border bg-background text-foreground z-50 rounded-md border px-3 py-1.5 text-sm font-medium shadow-md"
            >
              Merge
              <Tooltip.Arrow className="fill-background" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>

      <PopoverContent align="start" className="w-64 p-2">
        <p className="text-muted-foreground mb-2 px-2 text-xs font-medium">
          Merge into...
        </p>
        {targets.length === 0 ? (
          <p className="text-muted-foreground px-2 py-3 text-center text-sm">
            No lists available
          </p>
        ) : (
          <div className="max-h-48 overflow-y-auto">
            {targets.map((list) => (
              <button
                key={list.id}
                type="button"
                className="hover:bg-accent w-full rounded-sm px-2 py-1.5 text-left text-sm transition-colors"
                disabled={mergeMutation.isPending}
                onClick={() =>
                  mergeMutation.mutate({
                    sourceType,
                    sourceId,
                    targetListId: list.id,
                  })
                }
              >
                {list.name}
                <span className="text-muted-foreground ml-1 text-xs">
                  ({list.problemCount})
                </span>
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
